import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Shield, Users, CreditCard, FileText, Upload, LogOut, Trash2, Brain, RefreshCw, Download, Loader2, Ticket, Gift, X, Check, Settings, Calendar, Building2, Eye, EyeOff, Plus, Edit2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ setAdmin }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [counselingReports, setCounselingReports] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState({});
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const [grantingAccess, setGrantingAccess] = useState({});
  
  // New state for added features
  const [enrollment, setEnrollment] = useState({ students: [], total: 0, date_summary: {} });
  const [enrollmentDateRange, setEnrollmentDateRange] = useState({ start: '', end: '' });
  const [psychometricResults, setPsychometricResults] = useState([]);
  const [csrPartners, setCSRPartners] = useState([]);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [changingPassword, setChangingPassword] = useState(false);
  const [newCSRPartner, setNewCSRPartner] = useState({
    name: '', email: '', password: '', organization: '', 
    assigned_schools: '', assigned_coupons: ''
  });
  const [creatingPartner, setCreatingPartner] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  
  const [newQuestion, setNewQuestion] = useState({
    grade: '8',
    category: 'logical',
    question_text: '',
    options: ['', '', '', ''],
    correct_answer: 0,
    weight: 1.0
  });

  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_percent: 100,
    max_uses: '',
    expiry_date: '',
    description: ''
  });

  const token = localStorage.getItem('token');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, studentsRes, paymentsRes, attemptsRes, questionsRes, reportsRes, couponsRes, psychRes, csrRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard-stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/students`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/payments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/test-attempts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/questions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/counseling/reports`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { reports: [] } })),
        axios.get(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { coupons: [] } })),
        axios.get(`${API}/admin/psychometric-results`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { results: [] } })),
        axios.get(`${API}/admin/csr-partners`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { partners: [] } }))
      ]);

      setStats(statsRes.data);
      setStudents(studentsRes.data.students);
      setPayments(paymentsRes.data.payments);
      setAttempts(attemptsRes.data.attempts);
      setQuestions(questionsRes.data.questions);
      setCounselingReports(reportsRes.data.reports || []);
      setCoupons(couponsRes.data.coupons || []);
      setPsychometricResults(psychRes.data.results || []);
      setCSRPartners(csrRes.data.partners || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    setAdmin(null);
    navigate('/');
  };

  // Coupon Management Functions
  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/coupons`,
        {
          code: newCoupon.code,
          discount_percent: parseInt(newCoupon.discount_percent),
          max_uses: newCoupon.max_uses ? parseInt(newCoupon.max_uses) : null,
          expiry_date: newCoupon.expiry_date || null,
          description: newCoupon.description || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Coupon "${newCoupon.code.toUpperCase()}" created successfully`);
      setNewCoupon({ code: '', discount_percent: 100, max_uses: '', expiry_date: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleDeleteCoupon = async (code) => {
    if (!window.confirm(`Are you sure you want to deactivate coupon "${code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Coupon "${code}" deactivated`);
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate coupon');
    }
  };

  // Grant/Revoke Access Functions
  const handleGrantAccess = async (userId, studentName) => {
    setGrantingAccess(prev => ({ ...prev, [userId]: true }));
    try {
      await axios.post(
        `${API}/admin/grant-access/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Free access granted to ${studentName}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    } finally {
      setGrantingAccess(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleRevokeAccess = async (userId, studentName) => {
    if (!window.confirm(`Revoke access from ${studentName}?`)) return;
    setGrantingAccess(prev => ({ ...prev, [userId]: true }));
    try {
      await axios.post(
        `${API}/admin/revoke-access/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Access revoked from ${studentName}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    } finally {
      setGrantingAccess(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/questions`,
        newQuestion,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Question added successfully');
      setNewQuestion({
        grade: '8',
        category: 'logical',
        question_text: '',
        options: ['', '', '', ''],
        correct_answer: 0,
        weight: 1.0
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await axios.delete(`${API}/admin/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Question deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const handleRegenerateReport = async (reportId) => {
    setRegenerating(prev => ({ ...prev, [reportId]: true }));
    try {
      const response = await axios.post(
        `${API}/admin/counseling/regenerate-report/${reportId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Report regenerated for ${response.data.student_name}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to regenerate report');
    } finally {
      setRegenerating(prev => ({ ...prev, [reportId]: false }));
    }
  };

  const handleRegenerateAll = async () => {
    if (!window.confirm('This will regenerate ALL counseling reports with updated action plans. Continue?')) return;
    
    setRegeneratingAll(true);
    try {
      const response = await axios.post(
        `${API}/admin/counseling/regenerate-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to regenerate reports');
    } finally {
      setRegeneratingAll(false);
    }
  };

  const handleDownloadAdminPdf = async (reportId, studentName) => {
    try {
      const response = await axios.get(
        `${API}/counseling/report/${reportId}/download/admin`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Admin_Report_${studentName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Password Change Handler
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new !== passwordData.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: passwordData.current, new_password: passwordData.new },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully');
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Enrollment Data Handler
  const fetchEnrollment = async () => {
    try {
      const params = new URLSearchParams();
      if (enrollmentDateRange.start) params.append('start_date', enrollmentDateRange.start);
      if (enrollmentDateRange.end) params.append('end_date', enrollmentDateRange.end);
      
      const response = await axios.get(
        `${API}/admin/students-enrollment?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnrollment(response.data);
    } catch (error) {
      toast.error('Failed to fetch enrollment data');
    }
  };

  const downloadEnrollmentCSV = () => {
    if (enrollment.students.length === 0) {
      toast.error('No data to download');
      return;
    }
    const headers = ['Name', 'Email', 'Phone', 'Age', 'Grade', 'School', 'Enrollment Date', 'Coupon Used'];
    const rows = enrollment.students.map(s => [
      s.name, s.email, s.phone || '', s.age, s.grade, s.school || '',
      s.created_at ? new Date(s.created_at).toLocaleDateString() : '',
      s.coupon_used || ''
    ]);
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enrollment_${enrollmentDateRange.start || 'all'}_to_${enrollmentDateRange.end || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Enrollment data downloaded');
  };

  // CSR Partner Handlers
  const handleCreateCSRPartner = async (e) => {
    e.preventDefault();
    setCreatingPartner(true);
    try {
      await axios.post(
        `${API}/admin/csr-partners`,
        {
          name: newCSRPartner.name,
          email: newCSRPartner.email,
          password: newCSRPartner.password,
          organization: newCSRPartner.organization,
          assigned_schools: newCSRPartner.assigned_schools.split(',').map(s => s.trim()).filter(s => s),
          assigned_coupons: newCSRPartner.assigned_coupons.split(',').map(s => s.trim()).filter(s => s)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`CSR Partner "${newCSRPartner.name}" created`);
      setNewCSRPartner({ name: '', email: '', password: '', organization: '', assigned_schools: '', assigned_coupons: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create CSR partner');
    } finally {
      setCreatingPartner(false);
    }
  };

  const handleUpdateCSRPartner = async (partnerId) => {
    try {
      await axios.put(
        `${API}/admin/csr-partners/${partnerId}`,
        {
          assigned_schools: editingPartner.assigned_schools.split(',').map(s => s.trim()).filter(s => s),
          assigned_coupons: editingPartner.assigned_coupons.split(',').map(s => s.trim()).filter(s => s)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('CSR Partner updated');
      setEditingPartner(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update CSR partner');
    }
  };

  const handleDeleteCSRPartner = async (partnerId, partnerName) => {
    if (!window.confirm(`Deactivate CSR Partner "${partnerName}"?`)) return;
    try {
      await axios.delete(`${API}/admin/csr-partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`CSR Partner "${partnerName}" deactivated`);
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate CSR partner');
    }
  };

  // Download Psychometric Results
  const downloadPsychometricCSV = () => {
    if (psychometricResults.length === 0) {
      toast.error('No data to download');
      return;
    }
    const headers = ['Name', 'Email', 'Grade', 'Score %', 'Date'];
    const rows = psychometricResults.map(r => [
      r.user_name || '', r.user_email || '', r.grade, 
      r.percentage?.toFixed(1) || '',
      r.completed_at ? new Date(r.completed_at).toLocaleDateString() : ''
    ]);
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `psychometric_results_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Psychometric results downloaded');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Shield className="w-8 h-8 text-indigo-500" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Admin Dashboard</h1>
          </div>
          <Button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className="flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </nav>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-4 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: <Shield className="w-4 h-4" /> },
              { id: 'students', label: 'Students', icon: <Users className="w-4 h-4" /> },
              { id: 'enrollment', label: 'Enrollment', icon: <Calendar className="w-4 h-4" /> },
              { id: 'payments', label: 'Payments', icon: <CreditCard className="w-4 h-4" /> },
              { id: 'psychometric', label: 'Psychometric', icon: <FileText className="w-4 h-4" /> },
              { id: 'counseling', label: 'Career Mapping', icon: <Brain className="w-4 h-4" /> },
              { id: 'coupons', label: 'Coupons & Access', icon: <Ticket className="w-4 h-4" /> },
              { id: 'csr', label: 'CSR Partners', icon: <Building2 className="w-4 h-4" /> },
              { id: 'questions', label: 'Questions', icon: <Upload className="w-4 h-4" /> },
              { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> }
            ].map((tab) => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'enrollment') fetchEnrollment();
                }}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && stats && (
              <div className="grid md:grid-cols-4 gap-6">
                <div data-testid="stat-total-students" className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-6 h-6 text-indigo-500" />
                    <h3 className="font-heading font-semibold text-slate-900">Total Students</h3>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats.total_students}</p>
                </div>

                <div data-testid="stat-paid-students" className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <CreditCard className="w-6 h-6 text-teal-500" />
                    <h3 className="font-heading font-semibold text-slate-900">Paid Students</h3>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats.paid_students}</p>
                </div>

                <div data-testid="stat-test-attempts" className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <FileText className="w-6 h-6 text-amber-500" />
                    <h3 className="font-heading font-semibold text-slate-900">Test Attempts</h3>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats.total_attempts}</p>
                </div>

                <div data-testid="stat-revenue" className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <CreditCard className="w-6 h-6 text-purple-500" />
                    <h3 className="font-heading font-semibold text-slate-900">Revenue</h3>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">₹{stats.total_revenue}</p>
                </div>
              </div>
            )}

            {/* Students Tab */}
            {activeTab === 'students' && (
              <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Registered Students</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table data-testid="students-table" className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Name</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Email</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Grade</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Age</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Payment Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((student, index) => (
                          <tr key={student.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4 text-slate-700">{student.name}</td>
                            <td className="py-3 px-4 text-slate-700">{student.email}</td>
                            <td className="py-3 px-4 text-slate-700 capitalize">
                              {student.grade === 'graduation' ? 'Graduation' : `Grade ${student.grade}`}
                            </td>
                            <td className="py-3 px-4 text-slate-700">{student.age}</td>
                            <td className="py-3 px-4">
                              <span
                                className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                  student.has_paid
                                    ? 'bg-teal-100 text-teal-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}
                              >
                                {student.has_paid ? 'Paid' : 'Pending'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Payment Records</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table data-testid="payments-table" className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Order ID</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Amount</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Status</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((payment) => (
                          <tr key={payment.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4 text-slate-700 font-mono text-sm">
                              {payment.order_id}
                            </td>
                            <td className="py-3 px-4 text-slate-700">
                              ₹{(payment.amount / 100).toFixed(2)}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                  payment.status === 'success'
                                    ? 'bg-teal-100 text-teal-700'
                                    : payment.status === 'failed'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}
                              >
                                {payment.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-700">
                              {new Date(payment.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Test Attempts Tab */}
            {activeTab === 'attempts' && (
              <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Test Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table data-testid="attempts-table" className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Student Name</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Email</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Grade</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Score</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Percentage</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-900">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attempts.map((attempt) => (
                          <tr key={attempt.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4 text-slate-700">{attempt.user_name || 'N/A'}</td>
                            <td className="py-3 px-4 text-slate-700">{attempt.user_email || 'N/A'}</td>
                            <td className="py-3 px-4 text-slate-700 capitalize">
                              {attempt.grade === 'graduation' ? 'Graduation' : `Grade ${attempt.grade}`}
                            </td>
                            <td className="py-3 px-4 text-slate-700">
                              {attempt.score.toFixed(1)}/{attempt.max_score}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                  attempt.percentage >= 80
                                    ? 'bg-teal-100 text-teal-700'
                                    : attempt.percentage >= 60
                                    ? 'bg-indigo-100 text-indigo-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}
                              >
                                {attempt.percentage}%
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-700">
                              {new Date(attempt.completed_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Counseling Reports Tab */}
            {activeTab === 'counseling' && (
              <div className="space-y-6">
                {/* Regenerate All Banner */}
                <Card className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl border-0 shadow-lg">
                  <CardContent className="py-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold mb-1">Regenerate Reports</h3>
                        <p className="text-indigo-100 text-sm">
                          Update all existing Career Mapping reports with the fixed 30-Day Action Plan
                        </p>
                      </div>
                      <Button
                        data-testid="regenerate-all-btn"
                        onClick={handleRegenerateAll}
                        disabled={regeneratingAll}
                        className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold px-6"
                      >
                        {regeneratingAll ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Regenerating...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Regenerate All Reports
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Reports List */}
                <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">
                      Career Mapping Reports ({counselingReports.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {counselingReports.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Brain className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No counseling reports yet</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {counselingReports.map((report) => (
                          <div
                            key={report.report_id}
                            data-testid={`report-${report.report_id}`}
                            className="p-4 border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors"
                          >
                            <div className="flex flex-col md:flex-row justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-semibold text-slate-900">{report.user_name || 'Unknown'}</h4>
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    report.track === 'A' 
                                      ? 'bg-indigo-100 text-indigo-700' 
                                      : 'bg-purple-100 text-purple-700'
                                  }`}>
                                    Track {report.track}
                                  </span>
                                  {report.regenerated_at && (
                                    <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                                      Updated
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                                  <span><strong>Recommendation:</strong> {report.recommendation}</span>
                                  <span><strong>Cognitive:</strong> {report.cognitive_score}%</span>
                                  <span><strong>Date:</strong> {new Date(report.created_at).toLocaleDateString()}</span>
                                </div>
                                {report.gaps && report.gaps.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {report.gaps.map((gap) => (
                                      <span key={gap} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                                        {gap}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  data-testid={`download-report-${report.report_id}`}
                                  onClick={() => handleDownloadAdminPdf(report.report_id, report.user_name || 'Student')}
                                  variant="outline"
                                  size="sm"
                                  className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  PDF
                                </Button>
                                <Button
                                  data-testid={`regenerate-report-${report.report_id}`}
                                  onClick={() => handleRegenerateReport(report.report_id)}
                                  disabled={regenerating[report.report_id]}
                                  variant="outline"
                                  size="sm"
                                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                >
                                  {regenerating[report.report_id] ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <RefreshCw className="w-4 h-4 mr-1" />
                                      Regenerate
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Coupons & Access Tab */}
            {activeTab === 'coupons' && (
              <div className="space-y-6">
                {/* Create Coupon Form */}
                <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Ticket className="w-5 h-5 text-indigo-500" />
                      Create New Coupon
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateCoupon} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Coupon Code *</Label>
                          <Input
                            data-testid="coupon-code-input"
                            value={newCoupon.code}
                            onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                            placeholder="e.g., UXIMA100"
                            required
                            className="rounded-xl border-slate-200 bg-slate-50 uppercase"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Discount % (100 = Free) *</Label>
                          <Input
                            data-testid="coupon-discount-input"
                            type="number"
                            min="0"
                            max="100"
                            value={newCoupon.discount_percent}
                            onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: e.target.value })}
                            required
                            className="rounded-xl border-slate-200 bg-slate-50"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Max Uses (blank = unlimited)</Label>
                          <Input
                            data-testid="coupon-max-uses-input"
                            type="number"
                            min="1"
                            value={newCoupon.max_uses}
                            onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value })}
                            placeholder="Leave blank for unlimited"
                            className="rounded-xl border-slate-200 bg-slate-50"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Expiry Date (optional)</Label>
                          <Input
                            data-testid="coupon-expiry-input"
                            type="date"
                            value={newCoupon.expiry_date}
                            onChange={(e) => setNewCoupon({ ...newCoupon, expiry_date: e.target.value })}
                            className="rounded-xl border-slate-200 bg-slate-50"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Description (optional)</Label>
                        <Input
                          data-testid="coupon-description-input"
                          value={newCoupon.description}
                          onChange={(e) => setNewCoupon({ ...newCoupon, description: e.target.value })}
                          placeholder="e.g., School promotion, Free access for scholarship students"
                          className="rounded-xl border-slate-200 bg-slate-50"
                        />
                      </div>
                      <Button
                        type="submit"
                        data-testid="create-coupon-btn"
                        className="rounded-full px-8 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold"
                      >
                        <Ticket className="w-4 h-4 mr-2" />
                        Create Coupon
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Existing Coupons */}
                <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">Active Coupons ({coupons.filter(c => c.is_active).length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {coupons.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No coupons created yet</p>
                    ) : (
                      <div className="space-y-3">
                        {coupons.map((coupon) => (
                          <div
                            key={coupon.id}
                            data-testid={`coupon-${coupon.code}`}
                            className={`p-4 border rounded-xl flex justify-between items-center ${
                              coupon.is_active ? 'border-slate-200 bg-white' : 'border-red-200 bg-red-50 opacity-60'
                            }`}
                          >
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-lg text-indigo-600">{coupon.code}</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  coupon.discount_percent === 100 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {coupon.discount_percent === 100 ? 'FREE' : `${coupon.discount_percent}% OFF`}
                                </span>
                                {!coupon.is_active && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                                    INACTIVE
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-600">
                                Used: {coupon.current_uses}{coupon.max_uses ? `/${coupon.max_uses}` : ''} times
                                {coupon.expiry_date && ` • Expires: ${new Date(coupon.expiry_date).toLocaleDateString()}`}
                              </p>
                              {coupon.description && (
                                <p className="text-sm text-slate-500 mt-1">{coupon.description}</p>
                              )}
                            </div>
                            {coupon.is_active && (
                              <Button
                                data-testid={`delete-coupon-${coupon.code}`}
                                onClick={() => handleDeleteCoupon(coupon.code)}
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Manual Access Grant */}
                <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Gift className="w-5 h-5 text-teal-500" />
                      Grant Free Access to Students
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 mb-4">
                      Grant or revoke Career Mapping access for individual students. Students with access can start the test without payment.
                    </p>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {students.filter(s => s.grade && ['10', '11', '12'].includes(s.grade)).map((student) => (
                        <div
                          key={student.id}
                          data-testid={`student-access-${student.id}`}
                          className="p-3 border border-slate-200 rounded-xl flex justify-between items-center hover:border-indigo-200"
                        >
                          <div>
                            <span className="font-medium">{student.name}</span>
                            <span className="text-slate-500 text-sm ml-2">({student.email})</span>
                            <span className="text-slate-400 text-sm ml-2">Grade {student.grade}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {student.career_mapping_paid ? (
                              <>
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium flex items-center gap-1">
                                  <Check className="w-3 h-3" />
                                  Has Access
                                </span>
                                <Button
                                  data-testid={`revoke-access-${student.id}`}
                                  onClick={() => handleRevokeAccess(student.id, student.name)}
                                  variant="outline"
                                  size="sm"
                                  disabled={grantingAccess[student.id]}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  {grantingAccess[student.id] ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    'Revoke'
                                  )}
                                </Button>
                              </>
                            ) : (
                              <Button
                                data-testid={`grant-access-${student.id}`}
                                onClick={() => handleGrantAccess(student.id, student.name)}
                                variant="outline"
                                size="sm"
                                disabled={grantingAccess[student.id]}
                                className="text-teal-600 border-teal-200 hover:bg-teal-50"
                              >
                                {grantingAccess[student.id] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Gift className="w-4 h-4 mr-1" />
                                    Grant Free Access
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Questions Tab */}
            {activeTab === 'questions' && (
              <div className="space-y-6">
                {/* Add Question Form */}
                <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">Add New Question</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAddQuestion} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Grade</Label>
                          <select
                            data-testid="new-question-grade"
                            value={newQuestion.grade}
                            onChange={(e) => setNewQuestion({ ...newQuestion, grade: e.target.value })}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                          >
                            <option value="8">Grade 8</option>
                            <option value="9">Grade 9</option>
                            <option value="10">Grade 10</option>
                            <option value="11">Grade 11</option>
                            <option value="12">Grade 12</option>
                            <option value="graduation">Graduation</option>
                          </select>
                        </div>

                        <div className="space-y-2">
                          <Label>Category</Label>
                          <select
                            data-testid="new-question-category"
                            value={newQuestion.category}
                            onChange={(e) => setNewQuestion({ ...newQuestion, category: e.target.value })}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                          >
                            <option value="logical">Logical</option>
                            <option value="numerical">Numerical</option>
                            <option value="verbal">Verbal</option>
                            <option value="spatial">Spatial</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Question Text</Label>
                        <textarea
                          data-testid="new-question-text"
                          value={newQuestion.question_text}
                          onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                          rows={3}
                          required
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                        />
                      </div>

                      {newQuestion.options.map((option, index) => (
                        <div key={index} className="space-y-2">
                          <Label>Option {index + 1}</Label>
                          <Input
                            data-testid={`new-question-option-${index}`}
                            value={option}
                            onChange={(e) => {
                              const newOptions = [...newQuestion.options];
                              newOptions[index] = e.target.value;
                              setNewQuestion({ ...newQuestion, options: newOptions });
                            }}
                            required
                            className="rounded-xl border-slate-200 bg-slate-50"
                          />
                        </div>
                      ))}

                      <div className="space-y-2">
                        <Label>Correct Answer (0-3)</Label>
                        <Input
                          data-testid="new-question-correct-answer"
                          type="number"
                          min="0"
                          max="3"
                          value={newQuestion.correct_answer}
                          onChange={(e) => setNewQuestion({ ...newQuestion, correct_answer: parseInt(e.target.value) })}
                          required
                          className="rounded-xl border-slate-200 bg-slate-50"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Weight</Label>
                        <Input
                          data-testid="new-question-weight"
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={newQuestion.weight}
                          onChange={(e) => setNewQuestion({ ...newQuestion, weight: parseFloat(e.target.value) })}
                          required
                          className="rounded-xl border-slate-200 bg-slate-50"
                        />
                      </div>

                      <Button
                        type="submit"
                        data-testid="add-question-btn"
                        className="rounded-full px-8 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold"
                      >
                        Add Question
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Existing Questions */}
                <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">Existing Questions ({questions.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {questions.map((question) => (
                        <div
                          key={question.id}
                          data-testid={`question-${question.id}`}
                          className="p-4 border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                                  Grade {question.grade === 'graduation' ? 'Graduation' : question.grade}
                                </span>
                                <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium capitalize">
                                  {question.category}
                                </span>
                              </div>
                              <p className="text-slate-900 mb-2">{question.question_text}</p>
                              <p className="text-sm text-slate-600">
                                Correct Answer: Option {question.correct_answer + 1} | Weight: {question.weight}
                              </p>
                            </div>
                            <Button
                              data-testid={`delete-question-${question.id}`}
                              onClick={() => handleDeleteQuestion(question.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Enrollment Tab */}
            {activeTab === 'enrollment' && (
              <div className="space-y-6">
                <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading text-xl">Date-wise Student Enrollment</CardTitle>
                    <Button
                      data-testid="download-enrollment-csv"
                      onClick={downloadEnrollmentCSV}
                      variant="outline"
                      className="text-indigo-600 border-indigo-200"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-4 mb-6">
                      <div>
                        <Label>Start Date</Label>
                        <Input
                          type="date"
                          value={enrollmentDateRange.start}
                          onChange={(e) => setEnrollmentDateRange(prev => ({ ...prev, start: e.target.value }))}
                          className="w-40"
                        />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input
                          type="date"
                          value={enrollmentDateRange.end}
                          onChange={(e) => setEnrollmentDateRange(prev => ({ ...prev, end: e.target.value }))}
                          className="w-40"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button onClick={fetchEnrollment} className="bg-indigo-500 hover:bg-indigo-600">
                          Apply Filter
                        </Button>
                      </div>
                    </div>

                    <div className="text-sm text-slate-600 mb-4">
                      Total: <span className="font-bold text-indigo-600">{enrollment.total}</span> students
                    </div>

                    {enrollment.students.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Name</th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Email</th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Phone</th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Grade</th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">School</th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Coupon</th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Enrolled</th>
                            </tr>
                          </thead>
                          <tbody>
                            {enrollment.students.map((student) => (
                              <tr key={student.id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-3 px-4 font-medium text-slate-900">{student.name}</td>
                                <td className="py-3 px-4 text-slate-600">{student.email}</td>
                                <td className="py-3 px-4 text-slate-600">{student.phone || '-'}</td>
                                <td className="py-3 px-4">
                                  <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-sm">
                                    Grade {student.grade}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-slate-600">{student.school || '-'}</td>
                                <td className="py-3 px-4">
                                  {student.coupon_used ? (
                                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-mono">
                                      {student.coupon_used}
                                    </span>
                                  ) : '-'}
                                </td>
                                <td className="py-3 px-4 text-slate-500 text-sm">
                                  {student.created_at ? new Date(student.created_at).toLocaleDateString() : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No enrollment data. Click "Apply Filter" to load data.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Psychometric Results Tab */}
            {activeTab === 'psychometric' && (
              <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-xl">Psychometric Test Results ({psychometricResults.length})</CardTitle>
                  <Button
                    data-testid="download-psychometric-csv"
                    onClick={downloadPsychometricCSV}
                    variant="outline"
                    className="text-indigo-600 border-indigo-200"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                </CardHeader>
                <CardContent>
                  {psychometricResults.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Student</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Grade</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Score</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Categories</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {psychometricResults.map((result) => (
                            <tr key={result.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-4">
                                <div>
                                  <p className="font-medium text-slate-900">{result.user_name}</p>
                                  <p className="text-sm text-slate-500">{result.user_email}</p>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-sm">
                                  Grade {result.grade}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                  result.percentage >= 70 ? 'bg-green-100 text-green-700' :
                                  result.percentage >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {result.percentage?.toFixed(1)}%
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex flex-wrap gap-1">
                                  {result.category_scores && Object.entries(result.category_scores).map(([cat, scores]) => (
                                    <span key={cat} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                                      {cat}: {((scores.score / scores.max_score) * 100).toFixed(0)}%
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-slate-500 text-sm">
                                {result.completed_at ? new Date(result.completed_at).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No psychometric test results yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* CSR Partners Tab */}
            {activeTab === 'csr' && (
              <div className="space-y-6">
                <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Plus className="w-5 h-5" />
                      Create New CSR Partner
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateCSRPartner} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Partner Name *</Label>
                        <Input
                          value={newCSRPartner.name}
                          onChange={(e) => setNewCSRPartner(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={newCSRPartner.email}
                          onChange={(e) => setNewCSRPartner(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="partner@organization.com"
                          required
                        />
                      </div>
                      <div>
                        <Label>Password *</Label>
                        <Input
                          type="password"
                          value={newCSRPartner.password}
                          onChange={(e) => setNewCSRPartner(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Strong password"
                          required
                        />
                      </div>
                      <div>
                        <Label>Organization *</Label>
                        <Input
                          value={newCSRPartner.organization}
                          onChange={(e) => setNewCSRPartner(prev => ({ ...prev, organization: e.target.value }))}
                          placeholder="Organization Name"
                          required
                        />
                      </div>
                      <div>
                        <Label>Assigned Schools (comma-separated)</Label>
                        <Input
                          value={newCSRPartner.assigned_schools}
                          onChange={(e) => setNewCSRPartner(prev => ({ ...prev, assigned_schools: e.target.value }))}
                          placeholder="School A, School B"
                        />
                      </div>
                      <div>
                        <Label>Assigned Coupons (comma-separated)</Label>
                        <Input
                          value={newCSRPartner.assigned_coupons}
                          onChange={(e) => setNewCSRPartner(prev => ({ ...prev, assigned_coupons: e.target.value }))}
                          placeholder="LFS2026, UXIMAF"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Button
                          type="submit"
                          disabled={creatingPartner}
                          className="bg-teal-500 hover:bg-teal-600"
                        >
                          {creatingPartner ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                          Create CSR Partner
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>

                <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">CSR Partners ({csrPartners.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {csrPartners.length > 0 ? (
                      <div className="space-y-4">
                        {csrPartners.map((partner) => (
                          <div
                            key={partner.id}
                            className={`p-4 border rounded-xl ${partner.is_active === false ? 'border-red-200 bg-red-50' : 'border-slate-200 hover:border-teal-200'}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold text-slate-900">{partner.name}</h4>
                                  {partner.is_active === false && (
                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Inactive</span>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">{partner.email}</p>
                                <p className="text-sm text-slate-500">{partner.organization}</p>
                                
                                {editingPartner?.id === partner.id ? (
                                  <div className="mt-3 space-y-2">
                                    <div>
                                      <Label className="text-xs">Schools (comma-separated)</Label>
                                      <Input
                                        value={editingPartner.assigned_schools}
                                        onChange={(e) => setEditingPartner(prev => ({ ...prev, assigned_schools: e.target.value }))}
                                        className="text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Coupons (comma-separated)</Label>
                                      <Input
                                        value={editingPartner.assigned_coupons}
                                        onChange={(e) => setEditingPartner(prev => ({ ...prev, assigned_coupons: e.target.value }))}
                                        className="text-sm"
                                      />
                                    </div>
                                    <div className="flex gap-2">
                                      <Button size="sm" onClick={() => handleUpdateCSRPartner(partner.id)} className="bg-teal-500">
                                        <Check className="w-3 h-3 mr-1" /> Save
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => setEditingPartner(null)}>
                                        <X className="w-3 h-3 mr-1" /> Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="mt-2 space-y-1">
                                    <p className="text-xs text-slate-500">
                                      Schools: {partner.assigned_schools?.length > 0 ? partner.assigned_schools.join(', ') : 'None'}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      Coupons: {partner.assigned_coupons?.length > 0 ? partner.assigned_coupons.join(', ') : 'None'}
                                    </p>
                                  </div>
                                )}
                              </div>
                              
                              {partner.is_active !== false && !editingPartner && (
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingPartner({
                                      id: partner.id,
                                      assigned_schools: partner.assigned_schools?.join(', ') || '',
                                      assigned_coupons: partner.assigned_coupons?.join(', ') || ''
                                    })}
                                    className="text-slate-600"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteCSRPartner(partner.id, partner.name)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No CSR partners yet. Create one above.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm max-w-lg">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Change Password
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <Label>Current Password</Label>
                      <div className="relative">
                        <Input
                          type={showPasswords.current ? 'text' : 'password'}
                          value={passwordData.current}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, current: e.target.value }))}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                        >
                          {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label>New Password</Label>
                      <div className="relative">
                        <Input
                          type={showPasswords.new ? 'text' : 'password'}
                          value={passwordData.new}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, new: e.target.value }))}
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                        >
                          {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label>Confirm New Password</Label>
                      <div className="relative">
                        <Input
                          type={showPasswords.confirm ? 'text' : 'password'}
                          value={passwordData.confirm}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, confirm: e.target.value }))}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                        >
                          {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={changingPassword}
                      className="w-full bg-indigo-500 hover:bg-indigo-600"
                    >
                      {changingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Change Password
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
